import {StyleSheet, Text, View} from "react-native";
import {GoogleMap, Marker, OverlayView} from "@react-google-maps/api";
import React from "react";
import GoogleMapsStyle from "../../../../GoogleMapStyle";

const ADDRESS_LEVEL_ZOOM = 15;

const CampPageMap = ({camp, setSelectedMarker, isMapLoaded = false, mapLoadError = null}) => {
    const lat = typeof camp?.geolocation?.lat === 'number' ? camp.geolocation.lat : camp?.lat;
    const lng = typeof camp?.geolocation?.lng === 'number' ? camp.geolocation.lng : camp?.lng;
    const hasCoordinates = typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng);
    const primaryAddress = camp?.campAddress || [camp?.street, camp?.city, camp?.state, camp?.zipcode].filter(Boolean).join(', ');
    const secondaryAddress = [camp?.city, camp?.state, camp?.zipcode].filter(Boolean).join(', ');

    return (
        <View style={{flexDirection: 'column', marginTop: 30}}>
            <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Where you'll train</Text>
            </View>

            <View style={{flexDirection: 'row', width: '100%', marginBottom: 30}}>
                <View style={styles.mapContainer}>
                    {!hasCoordinates ? (
                        <View style={styles.mapFallback}>
                            <Text style={styles.mapFallbackTitle}>Map unavailable</Text>
                            <Text style={styles.mapFallbackText}>
                                This camp does not have a valid location pinned yet.
                            </Text>
                        </View>
                    ) : mapLoadError ? (
                        <View style={styles.mapFallback}>
                            <Text style={styles.mapFallbackTitle}>Map unavailable</Text>
                            <Text style={styles.mapFallbackText}>
                                Google Maps could not be loaded right now. Please try again in a moment.
                            </Text>
                        </View>
                    ) : !isMapLoaded ? (
                        <View style={styles.mapFallback}>
                            <Text style={styles.mapFallbackTitle}>Loading map...</Text>
                            <Text style={styles.mapFallbackText}>
                                Preparing the camp location.
                            </Text>
                        </View>
                    ) : (
                        <GoogleMap
                            center={{lat, lng}}
                            zoom={ADDRESS_LEVEL_ZOOM}
                            mapContainerStyle={{height: '100%', width: '100%'}}
                            options={{
                                styles: GoogleMapsStyle,
                                disableDefaultUI: true,
                                zoomControl: true,
                            }}
                        >
                            {!!primaryAddress && (
                                <OverlayView
                                    position={{lat, lng}}
                                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                    getPixelPositionOffset={(width, height) => ({
                                        x: -width - 18,
                                        y: -height + 18,
                                    })}
                                >
                                    <div
                                        style={{
                                            position: 'relative',
                                            maxWidth: '260px',
                                            minWidth: '180px',
                                            padding: '10px 12px',
                                            borderRadius: '14px',
                                            backgroundColor: 'rgba(255,255,255,0.98)',
                                            border: '1px solid rgba(229,231,235,0.92)',
                                            boxShadow: '0 10px 22px rgba(15, 23, 42, 0.14)',
                                            color: '#111827',
                                            fontFamily: 'Inter, Arial, sans-serif',
                                            lineHeight: 1.3,
                                            overflow: 'visible',
                                        }}
                                    >
                                        <div
                                            style={{
                                                position: 'absolute',
                                                right: '-6px',
                                                bottom: '14px',
                                                width: '12px',
                                                height: '12px',
                                                background: '#FFFFFF',
                                                borderRight: '1px solid rgba(229,231,235,0.95)',
                                                borderBottom: '1px solid rgba(229,231,235,0.95)',
                                                transform: 'rotate(-45deg)',
                                            }}
                                        />
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                minWidth: 0,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    marginBottom: '5px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '6px',
                                                        height: '6px',
                                                        borderRadius: '999px',
                                                        backgroundColor: '#2563EB',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <div
                                                    style={{
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.04em',
                                                        textTransform: 'uppercase',
                                                        color: '#64748B',
                                                    }}
                                                >
                                                    Camp Address
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    color: '#0F172A',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {primaryAddress}
                                            </div>
                                        </div>
                                    </div>
                                </OverlayView>
                            )}
                            <Marker
                                position={{lat, lng}}
                                onClick={() => setSelectedMarker(camp)}
                            />
                        </GoogleMap>
                    )}
                </View>
            </View>

            <View style={{flexDirection: 'row', alignItems: 'center'}}>
                <Text style={{fontSize: 16, marginRight: 5}}>
                    We've verified that this camp’s location is accurate.
                </Text>
                <Text style={{fontSize: 16, fontWeight: '500', textDecorationLine: 'underline'}}>
                    Learn more
                </Text>
            </View>
        </View>
    )
};

const styles = StyleSheet.create({
    sectionHeader: {
        marginBottom: 20,
    },
    sectionTitle: {
        fontSize: 20,
        fontWeight: '500',
        color: '#111827',
    },
    mapContainer: {
        flex: 2,
        height: 400,
        borderRadius: 10,
        overflow: 'hidden',
        justifyContent: 'center',
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },
    mapFallback: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 24,
        backgroundColor: '#F8FAFC',
    },
    mapFallbackTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#111827',
        marginBottom: 8,
    },
    mapFallbackText: {
        fontSize: 14,
        lineHeight: 22,
        color: '#6B7280',
        textAlign: 'center',
    },
});

export default CampPageMap;